import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UseFormReturn } from 'react-hook-form';
import { format } from 'date-fns';
import { LeadFormData } from '@/schemas/lead.schema';
import { useServiceTypes, useCampaigns, useLeadSources } from '@/hooks/useReferenceData';
import { useRecentLeadsByPhone } from '@/hooks/useRecentLeadsByPhone';
import { useMatchingCustomers } from '@/hooks/useMatchingCustomers';
import { CustomerPicker, type PickedCustomer } from './CustomerPicker';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { AddressVerificationBadge } from '@/components/ui/AddressVerificationBadge';
import type { NormalizedAddress } from '@/utils/parseGoogleAddress';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PhoneCall, AlertTriangle } from 'lucide-react';

const CALL_CLASSIFICATIONS = [
  { value: 'service_lead', label: '🔧 Service Lead', color: 'bg-success/10 text-success' },
  { value: 'general_inquiry', label: '❓ General Inquiry', color: 'bg-info/10 text-info' },
  { value: 'existing_customer', label: '🔄 Existing Customer', color: 'bg-primary/10 text-primary' },
  { value: 'vendor_call', label: '📦 Vendor / Supplier', color: 'bg-warning/10 text-warning' },
  { value: 'spam_junk', label: '🚫 Spam / Junk', color: 'bg-destructive/10 text-destructive' },
];

/** What the form needs to know about the customer a lead is pinned to. */
export type LinkedCustomer = Pick<PickedCustomer, 'id' | 'customer_name' | 'primary_phone' | 'company_name'>;

interface LeadFormProps {
  form: UseFormReturn<LeadFormData>;
  /** When editing, the lead being edited — excluded from the duplicate-phone warning. */
  currentLeadId?: string;
  /** Existing customer this lead will be attached to; null = new contact. */
  linkedCustomer?: LinkedCustomer | null;
  /** Called when the CSR picks (or unlinks) an existing customer. */
  onLinkCustomer?: (customer: LinkedCustomer | null) => void;
}

export function LeadForm({ form, currentLeadId, linkedCustomer = null, onLinkCustomer }: LeadFormProps) {
  const { locale } = useCompanyLocale();
  const { addressLabels, addressPlaceholders, postcode: postcodeRule } = locale;
  const { data: serviceTypes = [] } = useServiceTypes();
  const { data: campaigns = [] } = useCampaigns();
  const { data: leadSources = [] } = useLeadSources();
  const customerType = form.watch('customer_type');
  const selectedSourceId = form.watch('lead_source_id');
  const selectedCampaignId = form.watch('campaign_id');
  const phone = form.watch('customer_phone');
  const email = form.watch('customer_email');
  const canLink = !!onLinkCustomer && !currentLeadId;

  // The typed phone or email already belongs to a customer. The database will
  // link the lead to them on insert regardless; showing it here lets the CSR
  // confirm it (and pull the card's details in) before saving.
  const { data: matchingCustomers = [] } = useMatchingCustomers(phone, email, {
    enabled: canLink && !linkedCustomer,
  });
  const match = matchingCustomers[0];

  // Fill the contact fields from a customer card. Only non-empty values are
  // written, so a card with no email never blanks an email the CSR typed.
  const applyCustomer = (c: Partial<PickedCustomer> & LinkedCustomer) => {
    const set = (name: keyof LeadFormData, value: string | null | undefined) => {
      if (value) form.setValue(name, value as never, { shouldDirty: true, shouldValidate: true });
    };
    set('customer_type', c.customer_type);
    set('customer_name', c.customer_name);
    set('company_name', c.company_name);
    set('customer_phone', c.primary_phone);
    set('customer_email', c.primary_email);
    set('customer_address', c.primary_address);
    set('customer_suburb', c.suburb);
    set('customer_state', c.state);
    set('customer_postcode', c.postcode);
    onLinkCustomer?.({
      id: c.id,
      customer_name: c.customer_name,
      primary_phone: c.primary_phone,
      company_name: c.company_name ?? null,
    });
  };

  // Same number already rang in? Point the CSR at that lead so the campaign keeps the sale.
  const { data: recentLeads = [] } = useRecentLeadsByPhone(phone, { excludeLeadId: currentLeadId });
  const duplicate = recentLeads[0];
  // The database refuses a manual lead when the phone has a non-lost lead from the last 24h.
  const duplicateBlocks =
    !!duplicate && !currentLeadId && duplicate.status !== 'lost' &&
    Date.now() - new Date(duplicate.created_at).getTime() < 24 * 60 * 60 * 1000;

  // Campaign list narrows to the chosen source; picking a campaign sets its source.
  const visibleCampaigns = useMemo(
    () => (selectedSourceId ? campaigns.filter((c) => c.lead_source_id === selectedSourceId) : campaigns),
    [campaigns, selectedSourceId],
  );
  const campaignsBySource = visibleCampaigns.reduce((acc, campaign) => {
    const sourceName = campaign.lead_sources?.name || 'Other';
    if (!acc[sourceName]) acc[sourceName] = [];
    acc[sourceName].push(campaign);
    return acc;
  }, {} as Record<string, typeof campaigns>);

  useEffect(() => {
    if (!selectedCampaignId) return;
    const campaign = campaigns.find((c) => c.id === selectedCampaignId);
    if (campaign?.lead_source_id && campaign.lead_source_id !== selectedSourceId) {
      form.setValue('lead_source_id', campaign.lead_source_id, { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedCampaignId, campaigns, selectedSourceId, form]);

  // Called when user selects a Google prediction.
  // Uses setIfChanged to prevent false "Unsaved Changes" state.
  // isPartial guard: never overwrite existing non-empty field with empty string.
  const handleAddressSelected = (normalized: NormalizedAddress) => {
    const setIfChanged = (name: keyof LeadFormData, newVal: string) => {
      if (form.getValues(name) !== newVal) {
        form.setValue(name, newVal as never, { shouldDirty: true });
      }
    };

    setIfChanged('customer_address', normalized.street);

    // Only set split fields if the normalized value is non-empty (isPartial guard)
    if (normalized.suburb) setIfChanged('customer_suburb', normalized.suburb);
    if (normalized.state)  setIfChanged('customer_state',  normalized.state);
    if (normalized.postcode) setIfChanged('customer_postcode', normalized.postcode);

    // Coordinates: silent, no dirty flag
    if (normalized.lat != null) form.setValue('lat', normalized.lat, { shouldDirty: false });
    if (normalized.lng != null) form.setValue('lng', normalized.lng, { shouldDirty: false });
  };

  return (
    <div className="space-y-4">
      {/* Call Classification — first field for CSRs to classify the call */}
      <FormField
        control={form.control}
        name="call_classification"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <PhoneCall className="h-4 w-4" />
              Call Type
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value} defaultValue="service_lead">
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Classify this call" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {CALL_CLASSIFICATIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>What type of call is this?</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Attach to an existing customer instead of re-keying their details.
          Hidden while editing: the link is fixed once the lead exists. */}
      {canLink && (
        <CustomerPicker
          linked={linkedCustomer}
          onSelect={applyCustomer}
          onClear={() => onLinkCustomer?.(null)}
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="customer_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer Type *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} defaultValue="individual">
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customer_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{customerType === 'company' ? 'Contact Name' : 'Customer Name'} *</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {customerType === 'company' && (
          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="customer_phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone *</FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder={locale.phonePlaceholder} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customer_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* The phone or email typed above already belongs to a customer card. */}
        {canLink && !linkedCustomer && match && (
          <Alert className="col-span-2 border-info/40 bg-info/10">
            <AlertTriangle className="h-4 w-4 text-info" />
            <AlertTitle className="text-info">
              This {match.matched_on === 'phone+email' ? 'phone and email' : match.matched_on} belongs to an existing customer
            </AlertTitle>
            <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>
                <span className="font-medium">{match.customer_name}</span>
                {match.company_name ? ` · ${match.company_name}` : ''} · {match.primary_phone}
                {match.total_jobs_count ? ` · ${match.total_jobs_count} job${match.total_jobs_count === 1 ? '' : 's'}` : ''}.
                {' '}Link this lead to them so it shows on their card.
              </span>
              <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => applyCustomer(match)}>
                Use this customer
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="service_type_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Service Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {duplicate && (
          <Alert
            className={cn(
              'col-span-2 text-foreground',
              duplicateBlocks ? 'border-destructive/40 bg-destructive/10' : 'border-warning/40 bg-warning/10',
            )}
          >
            <AlertTriangle className={cn('h-4 w-4', duplicateBlocks ? 'text-destructive' : 'text-warning')} />
            <AlertTitle className={duplicateBlocks ? 'text-destructive' : 'text-warning'}>
              {duplicateBlocks ? 'This number already has a lead from the last 24 hours' : 'This number already has a recent lead'}
            </AlertTitle>
            <AlertDescription className="text-sm">
              {duplicate.lead_number} · {duplicate.customer_name} · {format(new Date(duplicate.created_at), 'd MMM, h:mm a')}
              {duplicate.campaign?.name ? ` · ${duplicate.campaign.name}` : ''} · {duplicate.status.replace(/_/g, ' ')}.
              {' '}{duplicateBlocks ? 'A new lead cannot be created for this number today. Open the existing lead to book, quote or update it.' : 'Convert that lead instead so the campaign keeps the credit.'}{' '}
              <Link to={`/leads/${duplicate.id}`} className="font-semibold underline underline-offset-2">
                Open {duplicate.lead_number}
              </Link>
            </AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="lead_source_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Lead source *</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  const campaign = campaigns.find((c) => c.id === form.getValues('campaign_id'));
                  if (campaign && campaign.lead_source_id !== value) {
                    form.setValue('campaign_id', '', { shouldDirty: true });
                  }
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Where did this lead come from?" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {leadSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="campaign_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Campaign</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={selectedSourceId ? 'Select campaign (optional)' : 'Select campaign'} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(campaignsBySource).map(([sourceName, sourceCampaigns]) => (
                    <SelectGroup key={sourceName}>
                      <SelectLabel className="font-semibold text-primary">{sourceName}</SelectLabel>
                      {sourceCampaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id} className="pl-6">
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="quoted_price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Estimated Job Value ($)</FormLabel>
              <FormControl>
                <Input type="number" step="0.01" placeholder="e.g., 450" {...field} />
              </FormControl>
              <FormDescription>Enter estimated value — even for lost leads</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="scheduled_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Scheduled Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="scheduled_time"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Scheduled Time</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g., 10:00 AM - 12:00 PM" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="customer_address"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Street Address</FormLabel>
            <FormControl>
              <AddressAutocomplete
                id="lead-customer-address"
                value={field.value ?? ''}
                onChange={field.onChange}
                onAddressSelected={handleAddressSelected}
                disabled={field.disabled}
                placeholder="Start typing a street address…"
              />
            </FormControl>
            <AddressVerificationBadge
              address={field.value}
              lat={form.watch('lat')}
              lng={form.watch('lng')}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="customer_suburb"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{addressLabels.locality}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={`e.g., ${addressPlaceholders.locality}`} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customer_state"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{addressLabels.region}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={`e.g., ${addressPlaceholders.region}`} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customer_postcode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{addressLabels.postcode}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={`e.g., ${postcodeRule.placeholder}`} maxLength={postcodeRule.maxLength} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="issue_description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Issue Description *</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Notes</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
